import React, { useState, useEffect, useRef } from 'react';

const UltraSecureDeviceGuard = ({ children }) => {
  const [deviceState, setDeviceState] = useState({
    isMobile: false,
    isTablet: false,
    isAuthentic: false,
    checks: {}
  });
  const [showWarning, setShowWarning] = useState(false);
  const intervalRef = useRef(null);
  const detectionHistory = useRef([]);

  // Get device info for display
  const getDeviceInfo = () => {
    const ua = navigator.userAgent;
    if (/iPhone/i.test(ua)) return 'iPhone';
    if (/iPad/i.test(ua)) return 'iPad';
    if (/Android/i.test(ua)) return 'Android';
    if (/Windows/i.test(ua)) return 'Windows Desktop';
    if (/Mac/i.test(ua)) return 'Mac Desktop';
    if (/Linux/i.test(ua)) return 'Linux Desktop';
    return 'Unknown Device';
  };

  // Advanced device detection with anti-spoofing measures
  const performDeviceAnalysis = () => {
    const checks = {
      // Basic detection
      userAgent: /iPhone|Android.*Mobile|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
      tabletUA: /iPad|Android(?!.*Mobile)|Tablet/i.test(navigator.userAgent),
      
      // Advanced screen analysis
      screenRatio: window.screen.width / window.screen.height,
      // Mobile screen sizes (up to 430px width)
      mobileScreenSize: window.screen.width <= 430 && window.screen.height <= 932,
      // Tablet screen sizes (431px to 1024px width)
      tabletScreenSize: window.screen.width > 430 && window.screen.width <= 1024,
      availScreen: window.screen.availWidth <= 1024,
      
      // Viewport vs screen consistency (catches responsive mode)
      viewportConsistency: Math.abs(window.innerWidth - window.screen.width) < 50,
      
      // Touch and interaction detection
      touchPoints: navigator.maxTouchPoints > 0,
      touchEvents: 'ontouchstart' in window,
      
      // Device-specific APIs
      orientation: typeof window.orientation !== 'undefined',
      deviceMotion: 'DeviceMotionEvent' in window,
      deviceOrientation: 'DeviceOrientationEvent' in window,
      
      // Hardware detection
      cores: navigator.hardwareConcurrency,
      memory: navigator.deviceMemory,
      connection: navigator.connection ? navigator.connection.effectiveType : null,
      
      // Anti-spoofing checks
      devTools: checkDevTools(),
      mouseEvents: checkMouseCapability(),
      keyboard: checkKeyboardPresence(),
      
      // Platform consistency
      platform: /iPhone|iPad|Android|Mobile/i.test(navigator.platform),
      vendor: /Apple|Google/i.test(navigator.vendor || ''),
      
      // Responsive mode detection
      responsiveMode: detectResponsiveMode(),
      
      // Browser specific checks
      chrome: /Chrome/.test(navigator.userAgent) && !!/Google Inc/.test(navigator.vendor),
      safari: /Safari/.test(navigator.userAgent) && /Apple/.test(navigator.vendor),
    };

    // Calculate authenticity score
    let mobileScore = 0;
    let tabletScore = 0;
    let authenticityScore = 0;

    // Core mobile indicators
    if (checks.userAgent) mobileScore += 20;
    if (checks.mobileScreenSize) mobileScore += 15;
    if (checks.touchPoints && checks.touchEvents) mobileScore += 15;
    if (checks.orientation) mobileScore += 10;
    if (checks.platform) mobileScore += 10;

    // Core tablet indicators
    if (checks.tabletUA) tabletScore += 25;
    if (checks.tabletScreenSize) tabletScore += 20;
    if (checks.touchPoints && checks.touchEvents) tabletScore += 15;
    if (checks.orientation) tabletScore += 10;
    if (checks.platform) tabletScore += 10;
    
    // Authenticity checks (penalize desktop/responsive mode)
    if (checks.viewportConsistency) authenticityScore += 20;
    if (!checks.devTools) authenticityScore += 25;
    if (!checks.mouseEvents) authenticityScore += 15;
    if (!checks.keyboard) authenticityScore += 15;
    if (!checks.responsiveMode) authenticityScore += 25;

    const isMobile = mobileScore >= 50;
    const isTablet = tabletScore >= 60;
    const isAuthentic = authenticityScore >= 70 && !checks.responsiveMode;

    // Store detection history
    detectionHistory.current.push({
      timestamp: Date.now(),
      isMobile,
      isTablet,
      isAuthentic,
      score: { mobile: mobileScore, tablet: tabletScore, authentic: authenticityScore }
    });

    // Keep only last 10 checks
    if (detectionHistory.current.length > 10) {
      detectionHistory.current = detectionHistory.current.slice(-10);
    }

    return { isMobile, isTablet, isAuthentic, checks, mobileScore, tabletScore, authenticityScore };
  };

  // Detect developer tools
  function checkDevTools() {
    const widthThreshold = window.outerWidth - window.innerWidth > 160;
    const heightThreshold = window.outerHeight - window.innerHeight > 160;
    return widthThreshold || heightThreshold;
  }

  // Check if mouse events are present (desktop indicator)
  function checkMouseCapability() {
    // Check if mouse events fire differently than touch
    let mouseDetected = false;
    const testHandler = () => { mouseDetected = true; };
    
    document.addEventListener('mouseover', testHandler, { once: true, passive: true });
    
    // Simulate mouse event
    const event = new MouseEvent('mouseover', { bubbles: true });
    document.dispatchEvent(event);
    
    document.removeEventListener('mouseover', testHandler);
    return mouseDetected;
  }

  // Detect keyboard presence
  function checkKeyboardPresence() {
    return window.navigator.keyboard !== undefined || 
           document.activeElement?.tagName === 'INPUT';
  }

  // Detect responsive design mode
  function detectResponsiveMode() {
    // Check for common responsive mode indicators
    const isExactMobileSize = (
      (window.innerWidth === 375 && window.innerHeight === 667) || // iPhone 6/7/8
      (window.innerWidth === 414 && window.innerHeight === 736) || // iPhone 6+/7+/8+
      (window.innerWidth === 375 && window.innerHeight === 812) || // iPhone X
      (window.innerWidth === 360 && window.innerHeight === 640)    // Android
    );
    
    // Check if screen and viewport dimensions don't match typical mobile/tablet patterns
    const screenViewportMismatch = (
      window.screen.width > 1200 && window.innerWidth < 1025
    );
    
    // Check for desktop user agent with mobile/tablet viewport
    const desktopUserAgent = /Windows|Mac|Linux/.test(navigator.userAgent) && 
                            !/Mobile|Android|iPhone|iPad/.test(navigator.userAgent);
    
    return isExactMobileSize || screenViewportMismatch || 
           (desktopUserAgent && window.innerWidth < 1025);
  }

  // Continuous monitoring with anti-tampering
  const startSecureMonitoring = () => {
    const performCheck = () => {
      const analysis = performDeviceAnalysis();
      
      setDeviceState(analysis);
      
      // Show warning if manipulation detected
      if ((analysis.isMobile || analysis.isTablet) && !analysis.isAuthentic) {
        setShowWarning(true);
        setTimeout(() => setShowWarning(false), 3000);
      }
      
      // Random interval to prevent prediction
      const nextCheck = 2000 + Math.random() * 3000;
      intervalRef.current = setTimeout(performCheck, nextCheck);
    };
    
    performCheck();
  };

  useEffect(() => {
    startSecureMonitoring();
    
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Re-check when tab becomes visible
        const analysis = performDeviceAnalysis();
        setDeviceState(analysis);
      }
    };
    
    const handleFocus = () => {
      // Re-check on focus
      setTimeout(() => {
        const analysis = performDeviceAnalysis();
        setDeviceState(analysis);
      }, 100);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    return () => {
      if (intervalRef.current) clearTimeout(intervalRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const deviceInfo = getDeviceInfo();

  // Show mobile/tablet content if either mobile OR tablet AND authentic
  if ((deviceState.isMobile || deviceState.isTablet) && deviceState.isAuthentic) {
    return (
      <div className="preptune-mobile-app">
        {children}
        {showWarning && (
          <div style={{
            position: 'fixed',
            top: '20px',
            left: '20px',
            right: '20px',
            background: '#fee2e2',
            color: '#dc2626',
            padding: '12px',
            borderRadius: '8px',
            fontSize: '14px',
            zIndex: 10000,
            textAlign: 'center',
            border: '1px solid #fecaca'
          }}>
            ⚠️ Device emulation detected
          </div>
        )}
      </div>
    );
  }

  // Show restriction using Preptune styling
  return (
    <div className="preptune-app">
      {/* Fixed Header */}
      <header style={{ 
        position: 'fixed',
        top: '0',
        left: '0',
        right: '0',
        zIndex: '1000',
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(0, 0, 0, 0.3)',
        padding: '8px 24px',
        display: 'flex',
        alignItems: 'center',
        height: '60px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <img src="/image/logo.png" alt="Preptune Logo" style={{ width: '28px', height: '28px' }} />
          <span style={{ 
            fontSize: '18px', 
            fontWeight: '600', 
            color: '#1f2937' 
          }}>
            Preptune
          </span>
        </div>
      </header>

      {/* Full Screen Mobile-Only Message */}
      <main style={{ 
        minHeight: '100vh',
        background: '#f5f7fa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '80px 20px 20px 20px'
      }}>
        <div style={{ 
          width: '100%',
          maxWidth: '600px',
          textAlign: 'center'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '40px 24px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            border: '1px solid #cbcacaff'
          }}>
              
           {/* Simple Icon */}
            <div className="preptune-card-icon">
              <svg className="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                <path d="M11.209 3.816a1 1 0 0 0-1.966.368l.325 1.74a5.338 5.338 0 0 0-2.8 5.762l.276 1.473.055.296c.258 1.374-.228 2.262-.63 2.998-.285.52-.527.964-.437 1.449.11.586.22 1.173.75 1.074l12.7-2.377c.528-.1.418-.685.308-1.27-.103-.564-.636-1.123-1.195-1.711-.606-.636-1.243-1.306-1.404-2.051-.233-1.085-.275-1.387-.303-1.587-.009-.063-.016-.117-.028-.182a5.338 5.338 0 0 0-5.353-4.39l-.298-1.592Z"/>
                <path fillRule="evenodd" d="M6.539 4.278a1 1 0 0 1 .07 1.412c-1.115 1.23-1.705 2.605-1.83 4.26a1 1 0 0 1-1.995-.15c.16-2.099.929-3.893 2.342-5.453a1 1 0 0 1 1.413-.069Z" clipRule="evenodd"/>
                <path d="M8.95 19.7c.7.8 1.7 1.3 2.8 1.3 1.6 0 2.9-1.1 3.3-2.5l-6.1 1.2Z"/>
              </svg>
            </div>

            <h3 className="preptune-card-title" style={{
              fontSize: '28px',
              fontWeight: '600',
              color: '#1f2937',
              margin: '0 0 16px 0'
            }}>
              Mobile & Tablet Only
            </h3>
            
            <p className="preptune-card-description" style={{
              fontSize: '16px',
              color: '#6b7280',
              lineHeight: '1.6',
              marginBottom: '30px',
            }}>
              Preptune is built for mobile and tablet devices. It works best on those devices for optimal experience.
            </p>


            {/* Detection Status */}
            {((deviceState.isMobile || deviceState.isTablet) || deviceState.checks?.responsiveMode) && (
              <div style={{
                background: '#fef3c7',
                border: '1px solid #f59e0b',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '20px',
                maxWidth: '320px',
                margin: '0 auto 20px auto',
                textAlign: 'left'
              }}>
                <div style={{
                  fontSize: '14px',
                  color: '#92400e',
                  marginBottom: '12px',
                  fontWeight: '600',
                  textAlign: 'center'
                }}>
                  🔍 Detection Status:
                </div>
                <div style={{
                  fontSize: '13px',
                  color: '#78350f',
                  lineHeight: '1.6'
                }}>
                  <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'flex-start' }}>
                    <span style={{ marginRight: '8px', color: '#9ca3af' }}>•</span>
                    <span>Mobile: {deviceState.isMobile ? '✅ Detected' : '❌ Not Detected'}</span>
                  </div>
                  <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'flex-start' }}>
                    <span style={{ marginRight: '8px', color: '#9ca3af' }}>•</span>
                    <span>Tablet: {deviceState.isTablet ? '✅ Detected' : '❌ Not Detected'}</span>
                  </div>
                  <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'flex-start' }}>
                    <span style={{ marginRight: '8px', color: '#9ca3af' }}>•</span>
                    <span>Authentic: {deviceState.isAuthentic ? '✅ Verified' : '❌ Emulation'}</span>
                  </div>
                  {deviceState.checks?.responsiveMode && (
                    <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                      <span style={{ marginRight: '8px', color: '#9ca3af' }}>•</span>
                      <span>⚠️ Responsive mode detected</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* How to Access Section */}
            <div style={{
              background: '#f8f9fa',
              border: '1px solid #bebebeff',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px',
              maxWidth: '320px',
              margin: '0 auto 24px auto',
              textAlign: 'left'
            }}>
              <div style={{
                fontSize: '14px',
                color: '#374151',
                marginBottom: '12px',
                fontWeight: '600',
                textAlign: 'center'
              }}>
                How to access:
              </div>
              <div style={{
                fontSize: '14px',
                color: '#5f6368',
                lineHeight: '1.6'
              }}>
                <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'flex-start' }}>
                  <span style={{ marginRight: '8px', color: '#9ca3af' }}>•</span>
                  <span>Open your browser</span>
                </div>
                <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'flex-start' }}>
                  <span style={{ marginRight: '8px', color: '#9ca3af' }}>•</span>
                  <span>Visit this URL link on your mobile or tablet device</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <span style={{ marginRight: '8px', color: '#9ca3af' }}>•</span>
                  <span>Enjoy the experience</span>
                </div>
              </div>
            </div>

            <p style={{
              fontSize: '13px',
              color: '#888c93ff',
              marginBottom: '20px',
              fontStyle: 'italic'
            }}>
              Thank you for understanding!
            </p>

            {/* Footer Copyright */}
            <div style={{
              borderTop: '1px solid #e9ecef',
              paddingTop: '16px',
              fontSize: '13px',
              color: '#36383dff'
            }}>
              © 2025 Preptune. All rights reserved.
            </div>
          </div>
        </div>
      </main>

      {/* Responsive CSS for better mobile handling */}
      <style dangerouslySetInnerHTML={{__html: `
        @media (max-height: 700px) {
          .preptune-app main > div > div {
            padding: 30px 25px !important;
          }
          
          .preptune-card-icon {
            width: 56px !important;
            height: 56px !important;
            margin-bottom: 16px !important;
          }
          
          .preptune-card-icon svg {
            width: 28px !important;
            height: 28px !important;
          }
          
          .preptune-card-title {
            font-size: 24px !important;
            margin-bottom: 12px !important;
          }
          
          .preptune-card-description {
            font-size: 14px !important;
            margin-bottom: 24px !important;
          }
        }
        
        @media (max-width: 768px) {
          .preptune-app main {
            padding: 80px 16px 16px 16px !important;
          }
          
          .preptune-app main > div > div {
            padding: 32px 24px !important;
          }
        }
        
        @media (max-width: 480px) {
          .preptune-app main {
            padding: 80px 12px 12px 12px !important;
          }
          
          .preptune-app main > div > div {
            padding: 24px 20px !important;
          }
          
          .preptune-card-title {
            font-size: 24px !important;
          }
          
          .preptune-card-description {
            font-size: 14px !important;
          }
          
          header {
            padding: 8px 16px !important;
          }
          
          header span {
            font-size: 16px !important;
          }
          
          header img {
            width: 24px !important;
            height: 24px !important;
          }
        }
        
        .preptune-app {
          pointer-events: all !important;
          visibility: visible !important;
          opacity: 1 !important;
          position: fixed !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          z-index: 999999 !important;
        }
        
        /* Hide content even if CSS is manipulated */
        .preptune-mobile-app {
          display: ${(deviceState.isMobile || deviceState.isTablet) && deviceState.isAuthentic ? 'block' : 'none'} !important;
        }
      `}} />
    </div>
  );
};

export default UltraSecureDeviceGuard;